import { TabConfig } from 'custom_hooks/data_hooks/data_source/useTabs';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { appendToRoute } from 'utils/RouteHelper';

export type RouteTabConfig = TabConfig &
    Readonly<{
        /**
         * If the tab route is enabled.
         */
        disabled?: boolean;
        ariaLabel?: string;
    }>;

export type RouteTab<TTabConfig extends RouteTabConfig = RouteTabConfig> = TTabConfig &
    Readonly<{
        /**
         * The URL associated with the tab.
         */
        url: string;
        /**
         * If the tab route is currently active.
         */
        isActive: boolean;
    }>;

export type RouteTabsHandle<TTabConfig extends RouteTabConfig> = Readonly<{
    /**
     * The current list of available tabs.
     */
    routeTabs: RouteTab<TTabConfig>[];
    /**
     * The currently selected tab route.
     */
    currentTab: RouteTab<TTabConfig> | undefined;
}>;

/**
 * Hook used for configuring routes associated with tabs.
 * Can be used with `TabRouter`.
 */
export function useRouteTabs<TTabConfig extends RouteTabConfig>(
    tabs: TTabConfig[],
): RouteTabsHandle<TTabConfig> {
    const { path, url } = useRouteMatch();
    const tabMatch = useRouteMatch<{ tabId?: string }>(`${path}/:tabId`);
    const urlTabId = tabMatch?.params.tabId;

    const routeTabs: RouteTab<TTabConfig>[] = useMemo(
        () =>
            tabs.map((tab) => ({
                ...tab,
                url: appendToRoute(url, tab.id),
                isActive: tab.id === urlTabId,
            })),
        [url, tabs, urlTabId],
    );

    const currentTab = useMemo(
        () => routeTabs.find(({ id }) => id === urlTabId),
        [routeTabs, urlTabId],
    );

    return { routeTabs, currentTab };
}
